import { CommonModule } from '@angular/common'
import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  Output,
  EventEmitter,
  inject,
} from '@angular/core'
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { Subject, takeUntil } from 'rxjs'
import { TranslationService } from '../../../../../services/translation.service'
import { FprResinRow } from '../../../../../types/reports'
import { ReportService } from '../../../../../services/report.service'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatChipsModule } from '@angular/material/chips'
import { MatIconModule } from '@angular/material/icon'

type ResinPatch = { id: string; fields: Partial<FprResinRow> }

@Component({
  selector: 'app-resins',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatChipsModule,
    MatIconModule,
  ],
  templateUrl: './resins.component.html',
  styleUrls: ['./resins.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResinsComponent implements OnChanges, OnDestroy {
  private fb = inject(FormBuilder)
  translationService = inject(TranslationService)
  private cdr = inject(ChangeDetectorRef)
  private reportService = inject(ReportService)

  @Input() rows: FprResinRow[] = []
  @Input() isEditMode = false

  @Output() patches = new EventEmitter<ResinPatch[]>()
  @Output() editing = new EventEmitter<boolean>()

  dataSource = new MatTableDataSource<FprResinRow>([])
  rootForm: FormGroup
  private readonly QUANTITY_KEYS: (keyof FprResinRow)[] = [
    'qtyManufactured',
    'qtyImported',
    'qtyPlacedOnMarket',
    'qtyAlberta',
    'qtyBritishColumbia',
    'qtyManitoba',
    'qtyNewBrunswick',
    'qtyNewfoundlandAndLabrador',
    'qtyNovaScotia',
    'qtyOntario',
    'qtyPrinceEdwardIsland',
    'qtyQuebec',
    'qtySaskatchewan',
    'qtyNorthwestTerritories',
    'qtyNunavut',
    'qtyYukon',
  ]
  get form(): FormArray<FormGroup> {
    return this.rootForm.get('rows') as FormArray<FormGroup>
  }

  displayedColumns: string[] = [
    // read-only columns (shown in the table template)
    'productIdentifier',
    'sku',
    'productName',
    'supplier',
    'resinType',
    'resinSource',
    'reportingDate',
    'productWeightKg',
    // editable qty columns
    'qtyManufactured',
    'qtyImported',
    'qtyPlacedOnMarket',
    'qtyAlberta',
    'qtyBritishColumbia',
    'qtyManitoba',
    'qtyNewBrunswick',
    'qtyNewfoundlandAndLabrador',
    'qtyNovaScotia',
    'qtyOntario',
    'qtyPrinceEdwardIsland',
    'qtyQuebec',
    'qtySaskatchewan',
    'qtyNorthwestTerritories',
    'qtyNunavut',
    'qtyYukon',
  ]

  // Last snapshot per row for minimal patch emission.
  private lastSnapshot = new Map<string, string>()
  private destroyed$ = new Subject<void>()
  private isEditing = false

  // Initialize the root form and wire valueChanges to emit minimal patches.
  constructor() {
    this.rootForm = this.fb.group({
      rows: this.fb.array<FormGroup>([]),
    })

    // Emit patches whenever any control changes and update editing state.
    this.rootForm.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.emitPatches()
        this.updateEditingState()
      })
  }

  // When @Input() rows change, rebuild the table, form, and snapshots.
  ngOnChanges(changes: SimpleChanges): void {
    if ('rows' in changes) {
      this.setRows(this.rows || [])
    }
  }

  // Tear down subscriptions to avoid leaks.
  ngOnDestroy(): void {
    this.destroyed$.next()
    this.destroyed$.complete()
  }

  // Optimize *ngFor rendering by tracking each row by id.
  trackByRowId = (_: number, r: FprResinRow) => r.id

  // Bind rows to the data table, rebuild the form array, and refresh per-row snapshots.
  private setRows(rows: FprResinRow[]): void {
    this.dataSource.data = rows

    // rebuild form
    const fa = this.form
    while (fa.length) fa.removeAt(0)
    for (const r of rows) fa.push(this.buildRowForm(r))

    // seed snapshots (only the editable subset)
    this.lastSnapshot.clear()
    for (const r of rows) {
      this.lastSnapshot.set(r.id, JSON.stringify(this.editableSubsetFromRow(r)))
    }

    this.updateEditingState()
    this.cdr.markForCheck()
  }

  // Build the reactive form group for a single resin row; only qty fields are editable.
  private buildRowForm(r: FprResinRow): FormGroup {
    const q = (v: number) => ({ value: v ?? 0, disabled: !this.isEditMode })
    return this.fb.group({
      id: [r.id], // for patching
      qtyManufactured: [q(r.qtyManufactured), [Validators.min(0)]],
      qtyImported: [q(r.qtyImported), [Validators.min(0)]],
      qtyPlacedOnMarket: [q(r.qtyPlacedOnMarket), [Validators.min(0)]],
      qtyAlberta: [q(r.qtyAlberta), [Validators.min(0)]],
      qtyBritishColumbia: [q(r.qtyBritishColumbia), [Validators.min(0)]],
      qtyManitoba: [q(r.qtyManitoba), [Validators.min(0)]],
      qtyNewBrunswick: [q(r.qtyNewBrunswick), [Validators.min(0)]],
      qtyNewfoundlandAndLabrador: [
        q(r.qtyNewfoundlandAndLabrador),
        [Validators.min(0)],
      ],
      qtyNovaScotia: [q(r.qtyNovaScotia), [Validators.min(0)]],
      qtyOntario: [q(r.qtyOntario), [Validators.min(0)]],
      qtyPrinceEdwardIsland: [q(r.qtyPrinceEdwardIsland), [Validators.min(0)]],
      qtyQuebec: [q(r.qtyQuebec), [Validators.min(0)]],
      qtySaskatchewan: [q(r.qtySaskatchewan), [Validators.min(0)]],
      qtyNorthwestTerritories: [
        q(r.qtyNorthwestTerritories),
        [Validators.min(0)],
      ],
      qtyNunavut: [q(r.qtyNunavut), [Validators.min(0)]],
      qtyYukon: [q(r.qtyYukon), [Validators.min(0)]],
    })
  }

  // Snapshot only the editable fields from a row object (used to seed snapshots).
  private editableSubsetFromRow(r: FprResinRow): Partial<FprResinRow> {
    return {
      qtyManufactured: r.qtyManufactured,
      qtyImported: r.qtyImported,
      qtyPlacedOnMarket: r.qtyPlacedOnMarket,
      qtyAlberta: r.qtyAlberta,
      qtyBritishColumbia: r.qtyBritishColumbia,
      qtyManitoba: r.qtyManitoba,
      qtyNewBrunswick: r.qtyNewBrunswick,
      qtyNewfoundlandAndLabrador: r.qtyNewfoundlandAndLabrador,
      qtyNovaScotia: r.qtyNovaScotia,
      qtyOntario: r.qtyOntario,
      qtyPrinceEdwardIsland: r.qtyPrinceEdwardIsland,
      qtyQuebec: r.qtyQuebec,
      qtySaskatchewan: r.qtySaskatchewan,
      qtyNorthwestTerritories: r.qtyNorthwestTerritories,
      qtyNunavut: r.qtyNunavut,
      qtyYukon: r.qtyYukon,
    }
  }

  private updateEditingState(): void {
    const anyDirty = this.form.controls.some((fg) => fg.dirty)

    if (anyDirty !== this.isEditing) {
      this.isEditing = anyDirty
      this.editing.emit(this.isEditing)
    }
  }

  // Coerce a control value to a number; treat empty/invalid values as 0.
  private toNumber(v: unknown): number {
    if (v === '' || v == null) return 0
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
  }

  // Snapshot only the editable fields from a form group (used for diffing).
  private editableSubsetFromForm(fg: FormGroup): Partial<FprResinRow> {
    const n = (k: string) => this.toNumber(fg.get(k)?.value)
    return {
      qtyManufactured: n('qtyManufactured'),
      qtyImported: n('qtyImported'),
      qtyPlacedOnMarket: n('qtyPlacedOnMarket'),
      qtyAlberta: n('qtyAlberta'),
      qtyBritishColumbia: n('qtyBritishColumbia'),
      qtyManitoba: n('qtyManitoba'),
      qtyNewBrunswick: n('qtyNewBrunswick'),
      qtyNewfoundlandAndLabrador: n('qtyNewfoundlandAndLabrador'),
      qtyNovaScotia: n('qtyNovaScotia'),
      qtyOntario: n('qtyOntario'),
      qtyPrinceEdwardIsland: n('qtyPrinceEdwardIsland'),
      qtyQuebec: n('qtyQuebec'),
      qtySaskatchewan: n('qtySaskatchewan'),
      qtyNorthwestTerritories: n('qtyNorthwestTerritories'),
      qtyNunavut: n('qtyNunavut'),
      qtyYukon: n('qtyYukon'),
    }
  }

  // Diff current values against the last snapshot and return only changed keys.
  private diffEditable(
    prevSnap: string | undefined,
    curr: Partial<FprResinRow>,
  ): Partial<FprResinRow> {
    const prev = prevSnap
      ? (JSON.parse(prevSnap) as Record<string, unknown>)
      : {}
    const next: Record<string, unknown> = {}
    for (const k of Object.keys(curr)) {
      if (curr[k as keyof typeof curr] !== prev[k]) {
        next[k] = curr[k as keyof typeof curr]
      }
    }
    return next as Partial<FprResinRow>
  }

  // Compare form values to snapshots and emit minimal patches to the parent.
  private emitPatches(): void {
    const patches: ResinPatch[] = []
    for (let i = 0; i < this.form.length; i++) {
      const fg = this.form.at(i)
      const id = fg.get('id')?.value as string
      if (!id) continue

      const curr = this.editableSubsetFromForm(fg)
      const prevSnap = this.lastSnapshot.get(id)
      const fields = this.diffEditable(prevSnap, curr)
      if (Object.keys(fields).length > 0) {
        patches.push({ id, fields })

        // update our snapshot optimistically so we don't re-emit the same changes
        const merged = prevSnap
          ? { ...JSON.parse(prevSnap), ...fields }
          : { ...fields }
        this.lastSnapshot.set(id, JSON.stringify(merged))
      }
    }

    if (patches.length > 0) {
      this.patches.emit(patches)
    }
  }

  rowHasAllZeroQuantities(row: FprResinRow): boolean {
    const fg = this.form.controls.find(
      (group) => group.get('id')?.value === row.id,
    )

    if (!fg) {
      return this.reportService.areAllQuantitiesZero(row)
    }

    const quantities: Partial<Record<keyof FprResinRow, number>> = {}
    for (const key of this.QUANTITY_KEYS) {
      const ctrl = fg.get(key as string)
      quantities[key] = this.toNumber(ctrl?.value)
    }

    return this.reportService.areAllQuantitiesZero({
      ...row,
      ...quantities,
    } as FprResinRow)
  }
}
